
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { cloneDeep, merge, omit, } from 'lodash';
import { BehaviorSubject, filter, finalize, of, switchMap, tap, map, debounceTime, Subject, iif, Observable, first, catchError, throwError } from 'rxjs';
import { ProcessingStatus } from 'src/app/core/enums/lead/processing-status.enum';
import { Lead } from 'src/app/core/models/lead/lead.model';
import { LobbyErrorCode } from 'src/app/infrastructure/lobby-proxy/enums/lobby-error-code.enum';
import { APP_CONFIG } from 'src/app/shared/configurations/configurations.factory';
import { Configurations } from 'src/app/shared/configurations/models/configurations.model';
import { LeadDryrunError } from 'src/app/shared/utils/pipes/dryrun-lead-error.pipe';
import { ContactValidations, validatePhoneNumber } from 'src/app/shared/utils/validations/contact.validations';
import { FiscalIdValidations } from 'src/app/shared/utils/validations/fiscal-id.validations';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { HumanizePhonePipe } from 'src/app/shared/utils/pipes/humanize-phone.pipe';
import { MatDialog } from '@angular/material/dialog';
import { AggregateVersionErrorDialogComponent } from '../../dialogs/aggregate-version-error-dialog/aggregate-version-error-dialog.component';
import { GlobalSpinnerActions } from '../../global-spinner/actions-types';
import { SearchSuggestion } from '../../inputs/autocomplete/search-strategy-base';
import { LeadInfoFormService } from './services/lead-info-form.service';
import { LeadFormBase } from '../lead-form-base';
import { BooleanRawvaluePipe } from 'src/app/shared/utils/pipes/boolean-rawvalue.pipe';
import { LobbyErrorHandlerService } from 'src/app/core/services/lobby-error-handler.service';
import { AutocompleteBusinessSectorSuggestionService } from 'src/app/shared/common-features/services/autocomplete-strategies/autocomplete-business-sector-suggestion.service';
import { AutocompleteProvinceSuggestionService } from 'src/app/shared/common-features/services/autocomplete-strategies/autocomplete-province-suggestion.service';
import { LeadActions } from 'src/app/core/events/lead.actions';

const phoneUtils = PhoneNumberUtil.getInstance()
const typingDebounceTime = 1000

export interface LeadInfoDetailsForm extends FormGroup {
  controls: {
    businessName: FormControl<string | null>
    taxName: FormControl<string | null>
    piva: FormControl<string | null>
    italianFiscalCode: FormControl<string | null>
    municipality: FormControl<string | null>
    province: FormControl<string | null>
    businessSector: FormControl<string | null>

    contacts: FormArray<FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>>
    rawvalues: FormArray<FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>>

    privacyAccepted: FormControl<boolean | null>
    acknowledgmentAccepted: FormControl<boolean | null>
    marketingAccepted: FormControl<boolean | null>

    comment: FormControl<string | null>
  }
}

@Component({
    selector: 'lead-info-form',
    templateUrl: './lead-info-form.component.html',
    styleUrls: ['./lead-info-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LeadInfoFormComponent extends LeadFormBase<LeadInfoDetailsForm> implements OnInit, OnChanges, AfterViewInit {

  @Input() set lead(lead: Lead | null) {
    if (lead) {
      this._lead = lead
      this.leadFirstState = cloneDeep(lead)
      this.form = this.createForm()
      this.subscribeToForm(this.form)
      this.initLobbyValidation(this.form)
      this.dirty.emit(false)
      console.log('lead', lead)
    } else {
      this._lead = undefined
      this.leadFirstState = undefined
      this.form = undefined
    }
  }

  get lead(): Lead | null { return this._lead! }

  @Input() readOnly: boolean = false
  @Input() showAutoAssignButton: boolean = true
  @Input() showFooterResume: boolean = true

  @Output() dirty = new EventEmitter<boolean>()
  @Output() cancel = new EventEmitter<void>()
  @Output() leadModified = new EventEmitter<void>()
  @Output() refresh = new EventEmitter<void>()

  @ViewChild('lobbyErrors', { read: ElementRef }) lobbyErrorsEl!: ElementRef<HTMLElement>

  leadFirstState: Lead | undefined
  lobbyErrors$ = new BehaviorSubject<LeadDryrunError[]>([])
  dryrunLead$ = new BehaviorSubject<Lead | null>(null)

  processingStatus = ProcessingStatus

  newContact = new FormGroup({ description: new FormControl<string>('', [Validators.required, Validators.maxLength(255)]), value: new FormControl<string>('', [ContactValidations.contactFormValidator(), Validators.required, Validators.maxLength(255)]) })
  newRawvalue = new FormGroup({ description: new FormControl<string>('', [Validators.required, Validators.maxLength(255)]), value: new FormControl<string>('', [Validators.required, Validators.maxLength(1024)]) })

  private _filterLobbyError: boolean = false
  private _nonValuableLobbyErrors: LobbyErrorCode[] = [LobbyErrorCode.InvalidLogic]

  constructor(private readonly appStore: Store,
    @Inject(APP_CONFIG) config: Configurations,
    private readonly leadInfoService: LeadInfoFormService,
    readonly autocompleteProvinceSuggestionService: AutocompleteProvinceSuggestionService,
    readonly autocompleteBusinessSectorSuggestionService: AutocompleteBusinessSectorSuggestionService,
    humanizePhonePipe: HumanizePhonePipe,
    private readonly booleanRawvaluePipe: BooleanRawvaluePipe,
    readonly matDialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly errorHandlerService: LobbyErrorHandlerService) {
    super(config, 1000, humanizePhonePipe)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.readOnly) this.form?.disable()
    this.cdr.markForCheck()
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.lobbyErrors$.pipe(filter(errors => errors.length > 0)).subscribe(() => {
      this.lobbyErrorsEl.nativeElement.scrollIntoView({ behavior: "smooth" })
    })
  }

  onSelectedProvinceSuggestion(suggestion: SearchSuggestion | null) {

    let lobbyValidationsOrEmpty$: Observable<any> = of(void 0)

    iif(() => !!suggestion,
      this.leadInfoService.getProvinces$(Number(suggestion?.id)).pipe(
        tap(province => {
          this.lead!.province = province
          if (this.lead!.municipality?.province.id != province?.id) {
            this.lead!.municipality = null
            this.form!.controls.municipality.setValue('', { emitEvent: false })
          }
        }),
        tap(() => lobbyValidationsOrEmpty$ = this.lobbyValidations$())
      ),
      of(void 0).pipe(
        tap(() => {
          const wasProvinceValued = !!this.lead!.province
          const wasMunicipalityValued = !!this.lead!.municipality
          this.lead!.province = null
          if (wasProvinceValued && !wasMunicipalityValued) lobbyValidationsOrEmpty$ = this.lobbyValidations$()
        })
      )
    ).pipe(
      tap(() => { this.form!.updateValueAndValidity(); this.form!.markAsDirty(); }),
      switchMap(() => lobbyValidationsOrEmpty$)
    ).subscribe()

  }

  onSelectedMunicipalitySuggestion(suggestion: SearchSuggestion | null) {
    let lobbyValidationsOrEmpty$: Observable<any> = of(void 0)

    iif(() => !!suggestion,
      this.leadInfoService.getMunicipality$(Number(suggestion?.id)).pipe(
        tap(municipality => {
          this.lead!.municipality = municipality
          this.lead!.province = municipality.province
          this.form!.controls.province.setValue(municipality.province.name, { emitEvent: false })
        }),
        tap(() => lobbyValidationsOrEmpty$ = this.lobbyValidations$())
      ),
      of(void 0).pipe(
        tap(() => {
          const wasMunicipalityValued = !!this.lead!.municipality
          this.lead!.municipality = null
          if (wasMunicipalityValued) lobbyValidationsOrEmpty$ = this.lobbyValidations$()
        })
      )
    ).pipe(
      tap(() => { this.form!.updateValueAndValidity(); this.form!.markAsDirty(); }),
      switchMap(() => lobbyValidationsOrEmpty$)
    ).subscribe()
  }

  onSelectedBusinessSectorSuggestion(suggestion: SearchSuggestion | null) {

    let lobbyValidationsOrEmpty$: Observable<any> = of(void 0)

    iif(() => !!suggestion,
      this.leadInfoService.getBusinessSector$(Number(suggestion?.id)).pipe(
        tap(businessSector => this.lead!.businessSector = businessSector),
        tap(() => lobbyValidationsOrEmpty$ = this.lobbyValidations$())
      ),
      of(void 0).pipe(
        tap(() => {
          const wasBusinessSectorValued = !!this.lead!.businessSector
          this.lead!.businessSector = null
          if (wasBusinessSectorValued) lobbyValidationsOrEmpty$ = this.lobbyValidations$()
        })
      )
    ).pipe(
      tap(() => this.form!.updateValueAndValidity()),
      switchMap(() => lobbyValidationsOrEmpty$)
    ).subscribe()
  }

  onCancelClick() {
    this.cancel.emit()
  }

  onSaveClick() {
    of(void 0).pipe(
      tap(() => this._filterLobbyError = true),
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => this.leadInfoService.updateLeadInfo$(this.lead!, this.leadFirstState!)),
      tap(() => this.appStore.dispatch(LeadActions.leadInfoModified(this.lead!))),
      this.errorHandlerService.handleError,
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
      catchError((e) => {
        this._filterLobbyError = false
        this.lobbyValidations$().subscribe()
        return throwError(() => e)
      }),

      tap(() => this.leadModified.emit())
    )
      .subscribe()
  }

  onRejectClick() {
    of(void 0).pipe(
      tap(() => this._filterLobbyError = true),
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => this.leadInfoService.rejectLead$(this.lead!.id)),
      tap(() => this.appStore.dispatch(LeadActions.leadRejected(this.lead!.id))),
      this.errorHandlerService.handleError,
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
      catchError((e) => {
        this._filterLobbyError = false
        this.lobbyValidations$().subscribe()
        return throwError(() => e)
      }),
      tap(() => this.leadModified.emit())
    )
      .subscribe()
  }

  onAutoOfferClick() {
    of(void 0).pipe(
      tap(() => this._filterLobbyError = true),
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => this.leadInfoService.updateLeadInfo$(this.lead!, this.leadFirstState!)),
      switchMap(() => this.leadInfoService.requestLeadAutoOffer$(this.lead!.id)),
      tap(() => this.appStore.dispatch(LeadActions.leadRequestedAutoAssign(this.lead!.id))),
      this.errorHandlerService.handleError,
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
      catchError((e) => {
        this._filterLobbyError = false
        this.lobbyValidations$().subscribe()
        return throwError(() => e)
      }),
      tap(() => this.leadModified.emit())
    )
      .subscribe()
  }

  override createForm(): LeadInfoDetailsForm {
    return new FormBuilder().group(
      {
        piva: new FormControl<string>(this.getPiva(this._lead!)?.value ?? '', [FiscalIdValidations.pivaFormValidator()]),
        italianFiscalCode: new FormControl<string>(this.getItalianFiscalCode(this._lead!)?.value ?? '', [FiscalIdValidations.italianFiscalCodeFormValidator()]),

        municipality: new FormControl<string>(this._lead!.municipality?.name ?? ''),
        province: new FormControl<string>(this._lead!.province?.name ?? this._lead!.municipality?.province?.name ?? ''),
        businessSector: new FormControl<string>(this._lead!.businessSector?.description ?? ''),

        contacts: new FormArray(
          [...this._lead!.contacts.map(c => new FormGroup({ description: new FormControl<string>({ value: c.description, disabled: true }), value: new FormControl<string>({ value: this.humanizePhonePipe.transform(c.value), disabled: true }) }))]
        ),

        rawvalues: new FormArray(
          [...this.filterConfiguredRawValues(this._lead!.rawValues).map(r => {
            const valueFormControl = new FormControl<string>(r.value)

            valueFormControl.valueChanges.pipe(
              filter(() => valueFormControl.valid),
              debounceTime(typingDebounceTime),
              switchMap(() => this.lobbyValidations$())
            ).subscribe()

            return new FormGroup(
              {
                description: new FormControl<string>({ value: r.description, disabled: true }),
                value: valueFormControl
              })
          })]
        ),



        privacyAccepted: new FormControl<boolean>(this.booleanRawvaluePipe.transform(this.getPrivacyAccepted(this._lead!)?.value), [Validators.maxLength(1024)]),
        acknowledgmentAccepted: new FormControl<boolean>(this.booleanRawvaluePipe.transform(this.getAcknowledgmentAccepted(this._lead!)?.value), [Validators.maxLength(1024)]),
        marketingAccepted: new FormControl<boolean>(this.booleanRawvaluePipe.transform(this.getMarketingAccepted(this._lead!)?.value), [Validators.maxLength(1024)]),
        taxName: new FormControl<string>(this.getTaxName(this._lead!)?.value ?? '', [Validators.maxLength(1024)]),

        businessName: new FormControl<string>(this._lead!.businessName ?? '', [Validators.maxLength(255)]),
        comment: new FormControl<string>(this._lead!.comment ?? '')
      }
    )
  }

  private initLobbyValidation(form: LeadInfoDetailsForm) {

    this.lobbyErrors$ = new BehaviorSubject<LeadDryrunError[]>([])

    // Triggers

    form.controls.businessName.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.businessName.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.taxName.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.taxName.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.piva.valueChanges
      .pipe(
        filter(() => form.controls.piva.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.italianFiscalCode.valueChanges
      .pipe(
        filter(() => form.controls.italianFiscalCode.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.comment.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.comment.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.contacts.valueChanges
      .pipe(
        debounceTime(typingDebounceTime / 2),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.rawvalues.valueChanges
      .pipe(
        debounceTime(typingDebounceTime / 2),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    // Observers

    // Aggregate version
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.InvalidAggregateVersion)),
      filter(errors => !!errors.length)
    ).subscribe(() => {
      const dialog = this.matDialog.open(AggregateVersionErrorDialogComponent, AggregateVersionErrorDialogComponent.createConfigurations())
      dialog.afterClosed().subscribe(() => this.refresh.emit())
    })

    // Duplicates contact point
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => this.form!.controls.contacts.controls.forEach(c => {
        c.setErrors(omit(c.errors, ['dryrunDuplicate']))
        c.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      })),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.LeadContactPointDuplicate)),
      filter(errors => !!errors.length)
    ).subscribe(contactDuplicateErrors => {
      this.form!.controls.contacts.controls
        .filter(c => contactDuplicateErrors.map(e => e.message).some(v => phoneUtils.isNumberMatch(v, c.controls.value.value!) >= 3 || v.trim().toLowerCase() == c.controls.value.value!.trim().toLowerCase()))
        .forEach(c => c.setErrors(merge(c.errors, { dryrunDuplicate: true })))
    })

    // Piva
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => {
        this.form!.controls.piva.setErrors(omit(this.form!.controls.piva.errors, ['lobbyInvalid']))
        this.form!.controls.piva.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      }),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.PartitaIvaInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(() => this.form!.controls.piva.setErrors(merge(this.form!.controls.piva.errors, { lobbyInvalid: true })))

    // Italian fiscal code
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => {
        this.form!.controls.italianFiscalCode.setErrors(omit(this.form!.controls.italianFiscalCode.errors, ['lobbyInvalid']))
        this.form!.controls.italianFiscalCode.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      }),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.CodiceFiscalePersonaFisicaInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(() => this.form!.controls.italianFiscalCode.setErrors(merge(this.form!.controls.italianFiscalCode.errors, { lobbyInvalid: true })))

    // Invalid rawvalues
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => this.form!.controls.rawvalues.controls.forEach(r => {
        r.setErrors(omit(r.errors, ['lobbyInvalid']))
        r.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      })),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.RawValueInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(rawvalueErrors => {
      this.form!.controls.rawvalues.controls
        .filter(r => rawvalueErrors.map(e => e.message).some(v => v.trim().toLowerCase() == r.controls.value.value!.trim().toLowerCase()))
        .forEach(r => r.setErrors(merge(r.errors, { lobbyInvalid: true })))
    })

    // Business sector
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => {
        this.form!.controls.businessSector.setErrors(omit(this.form!.controls.italianFiscalCode.errors, ['lobbyInvalid']))
        this.form!.controls.businessSector.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      }),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.BusinessSectorAtecoCodeInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(() => this.form!.controls.businessSector.setErrors(merge(this.form!.controls.businessSector.errors, { lobbyInvalid: true })))

  }

  override subscribeToForm(form: LeadInfoDetailsForm) {

    form.controls.businessName.valueChanges
      .subscribe(value => this.lead!.businessName = value!)

    form.controls.taxName.valueChanges
      .subscribe(value => this.setTaxName(value!))

    form.controls.piva.valueChanges
      .subscribe(value => this.setPiva(value!))

    form.controls.italianFiscalCode.valueChanges
      .subscribe(value => this.setItalianFiscalCode(value!))

    form.controls.comment.valueChanges
      .subscribe(value => this.lead!.comment = value!)

    form.controls.rawvalues.controls.forEach(c =>
      c.controls.value.valueChanges.subscribe(v =>
        this.filterConfiguredRawValues(this.lead!.rawValues)[form.controls.rawvalues.controls.indexOf(c)].value = v!
      )
    )

    form.valueChanges.pipe(
      filter(() => form.dirty),
      first()
    ).subscribe(() => this.dirty.emit(true))

  }

  private lobbyValidations$() {
    if (this.readOnly) return of(void 0)
    return this.leadInfoService.dryrunLeadInfo$(this.lead!, this.leadFirstState!).pipe(map(result => {

      if ((<LeadDryrunError[]>result).length !== undefined) {
        console.log('errors 2', result)
        this.lobbyErrors$.next((result as LeadDryrunError[]).filter(e => !this._nonValuableLobbyErrors.includes(e.errorCode)))
        this.dryrunLead$.next(null)
      }
      else {
        this.dryrunLead$.next(result as Lead)
        this.lobbyErrors$.next([])
      }

    }))
  }
}
